/**
 * VH-113 / VH-114: QR Per-Exercise Set Capture — public mobile-first page.
 *
 * Reached by scanning the per-exercise QR printed under VH-113. PUBLIC route
 * `/capture/:token` — works without login; the token is the capability that
 * scopes access to exactly one exercise. Kept alive for backwards compatibility
 * with sheets already printed under VH-113 (VH-114 AC-17).
 *
 * VH-114 refactor: the per-set / per-metric input UI now lives in the shared
 * `ExerciseCaptureBlock`, so this page is also type-aware — a cardio/bike token
 * renders metric fields instead of bogus reps/weight steppers (bonus fix).
 */
import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { useQuery, useMutation } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { getCapture, saveCapture } from '../../services/captureService'
import ExerciseCaptureBlock from './ExerciseCaptureBlock'
import {
  isBlockPerSet,
  buildSetRows,
  buildMetricValues,
  toNumberOrNull,
} from './exerciseCaptureUtils'

export default function CaptureExercise() {
  const { token } = useParams()
  const { t } = useTranslation()

  const [sets, setSets] = useState([])
  const [metricValues, setMetricValues] = useState({})
  const [saveResult, setSaveResult] = useState(null)

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['capture', token],
    queryFn: () => getCapture(token),
    retry: false,
  })

  const capture = data?.data
  // Parent program is SUBMITTED (finalized): render the sheet read-only.
  const locked = Boolean(capture?.locked)
  const perSet = capture ? isBlockPerSet(capture) : true

  // Initialise the editable rows once the planned sets / metrics arrive.
  useEffect(() => {
    if (!capture) return
    if (isBlockPerSet(capture)) {
      setSets(buildSetRows(capture.sets))
      setMetricValues({})
    } else {
      setMetricValues(buildMetricValues(capture))
      setSets([])
    }
  }, [capture])

  const mutation = useMutation({
    mutationFn: (payload) => saveCapture(token, payload),
    onSuccess: (res) => {
      setSaveResult(res?.data ?? null)
      // VH-207: a saved set turns an aim into a proven value — reload the marks.
      refetch()
    },
  })

  const handleSetChange = (index, field, value) => {
    setSaveResult(null)
    setSets((prev) => prev.map((s, i) => (i === index ? { ...s, [field]: value } : s)))
  }

  const handleMetricChange = (key, value) => {
    setSaveResult(null)
    setMetricValues((prev) => ({ ...prev, [key]: value }))
  }

  const handleSave = () => {
    // Defensive: never POST against a finalized program (UI already disables
    // the button, but guard the handler too).
    if (locked) return
    setSaveResult(null)
    if (perSet) {
      mutation.mutate({
        sets: sets.map((s) => ({
          set_number: s.set_number,
          set_type: s.set_type,
          actual_reps: toNumberOrNull(s.actual_reps),
          actual_weight: toNumberOrNull(s.actual_weight),
          weight_notation: s.weight_notation === '' ? null : s.weight_notation,
        })),
      })
    } else {
      const cleaned = {}
      Object.entries(metricValues).forEach(([key, value]) => {
        if (value !== '' && value !== null && value !== undefined) {
          const n = Number(value)
          cleaned[key] = Number.isFinite(n) ? n : value
        }
      })
      mutation.mutate({ metric_values: cleaned })
    }
  }

  // --- Loading ---------------------------------------------------------------
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div
          role="status"
          aria-label={t('common.loading')}
          className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"
        />
      </div>
    )
  }

  // --- Invalid token (404) ---------------------------------------------------
  if (isError) {
    const status = error?.response?.status
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <div className="max-w-sm w-full bg-surface-container rounded-xl shadow p-6 text-center">
          <h1 className="text-lg font-semibold text-gray-900 mb-2">
            {status === 404 ? t('capture.invalid.title') : t('capture.error.title')}
          </h1>
          <p className="text-sm text-gray-600">
            {status === 404 ? t('capture.invalid.message') : t('capture.error.message')}
          </p>
        </div>
      </div>
    )
  }

  // --- Capture form ----------------------------------------------------------
  return (
    <div className="min-h-screen bg-gray-50 pb-28">
      <div className="max-w-md mx-auto px-4 pt-6">
        {/* Header */}
        <header className="mb-5">
          <h1 className="text-xl font-bold text-gray-900">{capture.exercise_name}</h1>
          <p className="mt-1 text-sm text-gray-600">
            {t('capture.weekLabel', { week: capture.week_number })}
            {capture.session_date && (
              <>
                {' · '}
                {capture.session_date}
              </>
            )}
          </p>
        </header>

        {/* Finalized (read-only) banner */}
        {locked && (
          <div
            role="status"
            className="mb-4 rounded-lg bg-amber-50 border border-amber-200 px-4 py-3 text-sm font-medium text-amber-800"
          >
            {t('capture.lockedBanner')}
          </div>
        )}

        <ExerciseCaptureBlock
          block={capture}
          sets={sets}
          metricValues={metricValues}
          locked={locked}
          onSetChange={handleSetChange}
          onMetricChange={handleMetricChange}
        />

        {/* Inline confirmation / error */}
        {saveResult && (
          <div className="mt-4 rounded-lg bg-green-50 border border-green-200 px-4 py-3 text-sm text-green-800">
            <p className="font-medium">{t('capture.saved', { count: saveResult.saved_count })}</p>
            {saveResult.session_completed && <p className="mt-1">{t('capture.sessionCompleted')}</p>}
          </div>
        )}
        {/* Non-blocking validation warnings (still saved). */}
        {saveResult?.warnings?.length > 0 && (
          <div className="mt-4 rounded-lg bg-amber-50 border border-amber-200 px-4 py-3 text-sm text-amber-800">
            <p className="font-medium">{t('capture.warningsTitle')}</p>
            <ul className="mt-1 list-disc list-inside space-y-1">
              {saveResult.warnings.map((w, i) => (
                <li key={i}>{w}</li>
              ))}
            </ul>
          </div>
        )}
        {mutation.isError && (
          <div className="mt-4 rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-800">
            {mutation.error?.response?.status === 409
              ? t('capture.finalizedError')
              : t('capture.saveError')}
          </div>
        )}
      </div>

      {/* Sticky Save button */}
      <div className="fixed bottom-0 inset-x-0 bg-surface-container border-t border-gray-200 px-4 py-3">
        <div className="max-w-md mx-auto">
          <button
            type="button"
            onClick={handleSave}
            disabled={mutation.isPending || locked}
            className="w-full rounded-lg bg-primary-600 text-on-primary font-semibold py-3 text-base disabled:opacity-60 disabled:cursor-not-allowed hover:bg-primary-700"
          >
            {mutation.isPending ? t('capture.saving') : t('capture.save')}
          </button>
        </div>
      </div>
    </div>
  )
}
